import { graphqlClient } from 'src/graphql/client'
import {
  GetCoachingBadgesDocument,
  GetCoachingBadgesQueryVariables,
  GetPlanMoveDocument,
  GetPlanMoveQueryVariables,
} from 'src/graphql/generated/graphql'
import { Store } from 'src/redux/store'
import { saleorGraphQLClient } from 'src/saleor_graphql/client'
import {
  AddCheckoutLinesDocument,
  AddCheckoutLinesMutationVariables,
  CheckoutDocument,
  CheckoutQueryVariables,
  CreateCheckoutDocument,
  CreateCheckoutMutationVariables,
  DeleteCheckoutLinesDocument,
  DeleteCheckoutLinesMutationVariables,
  GetSaleorCustomerDocument,
  UpdateSaleorCustomerDocument,
  UpdateSaleorCustomerMutationVariables,
} from 'src/saleor_graphql/generated/graphql'

type SuccessfulOAuthResponse = {
  success: true
  access_token: string
  expires_in: number
  token_type: 'Bearer'
  refresh_token: string
  id_token: string
  error?: never
}
type FailedOauthResponse = {
  success: false
  access_token?: never
  expires_in?: never
  token_type?: never
  id_token?: never
  error: string
}

export const getCoachingBadges = async (
  variables: GetCoachingBadgesQueryVariables,
) => {
  return await graphqlClient.request(GetCoachingBadgesDocument, variables)
}

export const getPlanMove = async (variables: GetPlanMoveQueryVariables) => {
  return await graphqlClient.request(GetPlanMoveDocument, variables)
}

export const getSaleorAccessToken = async (
  authorizationCode: string,
  codeVerifier: Promise<string>,
  redirectUri: string,
) => {
  const formData = new URLSearchParams()
  formData.append('client_id', `${process.env.REACT_APP_SALEOR_OIDC_CLIENT_ID}`)
  formData.append('code', authorizationCode)
  formData.append('code_verifier', await codeVerifier)
  formData.append('redirect_uri', redirectUri)
  formData.append('grant_type', 'authorization_code')

  const url = `${process.env.REACT_APP_API_URL}/oauth2/token/`

  try {
    const tokenResponse = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    })

    if (tokenResponse.status === 200) {
      const json = await tokenResponse.json()

      return ({
        success: true,
        ...json,
      } as unknown) as SuccessfulOAuthResponse
    }
    const json = await tokenResponse.json()

    return ({ success: false, ...json } as unknown) as FailedOauthResponse
  } catch (err) {
    console.error(err)
    throw err
  }
}

export const getSaleorRefreshToken = async (
  refreshToken: string,
  codeVerifier: Promise<string>,
  redirectUri: string,
) => {
  const token = Store.getState().user.controlData.token
  const formData = new URLSearchParams()
  formData.append('client_id', `${process.env.REACT_APP_SALEOR_OIDC_CLIENT_ID}`)
  formData.append('refresh_token', refreshToken)
  formData.append('code_verifier', await codeVerifier)
  formData.append('redirect_uri', redirectUri)
  formData.append('grant_type', 'refresh_token')

  const url = `${process.env.REACT_APP_API_URL}/oauth2/token/`

  try {
    const tokenResponse = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    })

    if (tokenResponse.status === 200) {
      const json = await tokenResponse.json()

      return ({
        success: true,
        ...json,
      } as unknown) as SuccessfulOAuthResponse
    }
    const json = await tokenResponse.json()

    return ({ success: false, ...json } as unknown) as FailedOauthResponse
  } catch (err) {
    console.error(err)
    throw err
  }
}

export const getCheckout = async (variables: CheckoutQueryVariables) => {
  const res = await saleorGraphQLClient.request(CheckoutDocument, variables)
  return res.checkout
}

export const createCheckout = async (
  variables: CreateCheckoutMutationVariables,
) => {
  const res = await saleorGraphQLClient.request(
    CreateCheckoutDocument,
    variables,
  )
  return res.checkoutCreate
}

export const addCheckoutLines = async (
  variables: AddCheckoutLinesMutationVariables,
) => {
  const res = await saleorGraphQLClient.request(
    AddCheckoutLinesDocument,
    variables,
  )

  return res.checkoutLinesAdd
}

export const getSaleorCustomer = async () => {
  // TODO need to attach saleor auth token here
  const token = 'get token from local storage'
  const res = await saleorGraphQLClient.request(
    GetSaleorCustomerDocument,
    undefined,
    { Authorization: `Bearer ${token}` },
  )

  return res.me
}

export const updateSaleorCustomer = async (
  variables: UpdateSaleorCustomerMutationVariables,
) => {
  // TODO need to attach saleor auth token here
  const token = 'get token from local storage'
  const res = await saleorGraphQLClient.request(
    UpdateSaleorCustomerDocument,
    variables,
    { Authorization: `Bearer ${token}` },
  )
  return res.customerUpdate
}

export const deleteCheckoutLines = async (
  variables: DeleteCheckoutLinesMutationVariables,
) => {
  const res = await saleorGraphQLClient.request(
    DeleteCheckoutLinesDocument,
    variables,
  )
  return res.checkoutLinesDelete
}
